import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { usePolicyDetails } from '@/features/air/policy-management/queries'
import { policyToTemplateValues } from '@/features/air/policy-management/serialization/form'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import {
	Section,
	SectionDescription,
	SectionTitle,
} from '@/features/app-shell/section'

import { ROLES } from '@/shared/auth/constants'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import BackLink from '@/components/common/back-link'
import { ErrorBoundary } from '@/components/common/error-boundary'
import { Skeleton } from '@/components/ui/skeleton'

const CustomPolicyEditor = lazy(
	() =>
		import('@/features/air/policy-management/components/custom-policy-editor'),
)

// `from` is a backend policy slug (e.g. `microsoft:azure:multifactor-...`),
// NOT a UUID. Keep it a plain string — UUID validation would reject every
// valid template deep-link.
const searchSchema = z.object({ from: z.string().optional() })

export const Route = createFileRoute('/_dashboard/air/policy-management/new')({
	validateSearch: zodValidator(searchSchema),

	beforeLoad: () => roleAuthGuard(ROLES.Analyst),

	head: () => ({
		meta: [
			{
				title: 'New Policy | Policy Management | BitLyft AIR®',
			},
		],
	}),
	component: RouteComponent,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const { from } = Route.useSearch()

	return (
		<Section>
			<div className="flex items-start gap-4">
				<BackLink to="/air/policy-management" />

				<div className="flex flex-col gap-1">
					<SectionTitle>New Policy</SectionTitle>
					<SectionDescription>Create a new custom policy</SectionDescription>
				</div>
			</div>

			<ErrorBoundary>
				<Suspense fallback={<EditorSkeleton />}>
					{from ? (
						<TemplateEditor from={from} />
					) : (
						<CustomPolicyEditor mode="create" />
					)}
				</Suspense>
			</ErrorBoundary>
		</Section>
	)
}

function TemplateEditor({ from }: { from: string }) {
	const { data } = usePolicyDetails(from)
	const source = data.rule

	return (
		<CustomPolicyEditor
			mode="template"
			policy={source}
			defaultValues={policyToTemplateValues(source)}
		/>
	)
}

function EditorSkeleton() {
	return (
		<div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
			<div className="flex flex-col gap-6">
				<Skeleton className="h-80 w-full" />
				<Skeleton className="h-[600px] w-full" />
				<Skeleton className="h-96 w-full" />
			</div>
			<Skeleton className="h-[500px] w-full" />
		</div>
	)
}
